import { ChangeDetectorRef, Component, ElementRef, Inject, Input, NgZone, OnDestroy, OnInit, Optional } from "@angular/core";
import { RtlService } from "../../../../../rtl/services/rtl.service";
import {
	WINDOW_LISTENER_TOKEN,
	WindowHostListenerService
} from "../../../../../window/window.host.listener.service";
import { DeltaEvent } from "../../../../directives/touch.drag/delta.drag.directive";
import { AbstractPopupComponent, MIN_PERCENT_GAP, MIN_PX_GAP } from "../abstract.popup.component";
import { PopupPositionStore } from "../popup.position.store";

const PX = 'px';

const GUARD_MIN_WIDTH=50;
const GUARD_MIN_HEIGHT=50;

@Component({
	selector: 'cp-resizable-popup',
	styleUrls: [
		'../popup.component.scss',
		'./popup.component.resize.scss'
	],
	templateUrl: '../popup.component.html'
})
export class ResizablePopupComponent extends AbstractPopupComponent implements OnInit, OnDestroy {
	@Input() label;
	@Input() labelParam: string;
	@Input() labelPlain:string;

	@Input() resizable;

	@Input() width: string;
	@Input() height: string;

	@Input() minHeight: string;
	@Input() minWidth: string;

	@Input() show: boolean;

	@Input() draggable: boolean;

	@Input() contentPadding: boolean;

	@Input() isModal=true;

	@Input() spinner:boolean;

	autoHeight;
	autoHeightAutoReCalc;

	protected _widthInPx: number;
	protected _heightInPx: number;
	protected _topInPx: number;
	protected _leftInPx: number;

	constructor(protected ngZone: NgZone,protected cd: ChangeDetectorRef,
				@Optional() protected positionStore:PopupPositionStore,// not available before login
				@Inject(WINDOW_LISTENER_TOKEN) protected windowsEventsListener:WindowHostListenerService,
				protected el:ElementRef,rtl:RtlService) {
		super(ngZone,cd,positionStore,el,windowsEventsListener,rtl);
	}

	ngOnChanges(){
		this.minHeight = this.minHeight || "300px";
		this.minWidth = this.minWidth || "300px";
	}
	ngOnInit() {
		const windowHeight = window.innerHeight;
		const windowWidth = window.innerWidth;

		let heightRatio, widthRatio;

		this.width = this.width || '1700px';
		this.height = this.height || '870px';

		if (this.height.endsWith(PX)) {
			this._heightInPx = +this.height.substring(0, this.height.length - 2);
			heightRatio = this._heightInPx / windowHeight;
		} else {
			let heightInPercents = +this.height.substring(0, this.height.length - 1);
			heightRatio = heightInPercents / 100;
			this._heightInPx = heightRatio * windowHeight;
		}

		if (this.width.endsWith(PX)) {
			this._widthInPx = +this.width.substring(0, this.width.length - 2);
			widthRatio = +this.width.substring(0, this.width.length - 2) / windowWidth;
		} else {
			this._widthInPx = +this.width.substring(0, this.width.length - 1) / 100 * windowWidth;
			widthRatio = +this.width.substring(0, this.width.length - 1) / 100;
		}

		let positionCache=this.positionStore?.getPoistion(this.positionToken);
		if (positionCache){
			this._leftInPx=positionCache.leftPx;
			this._topInPx=positionCache.topPx;
			if (positionCache.size){
				this._heightInPx=positionCache.size.heightPx;
				this._widthInPx=positionCache.size.widthPx;
			}
		}else{
			this._topInPx = (heightRatio > 0.98 ? 0.01 : (1 - heightRatio) / 2) * windowHeight;
			this._leftInPx = (widthRatio > 0.98 ? 0.01 : (1 - widthRatio) / 2) * windowWidth;
		}
	}

	get Top() {
		return this.getNormalizedDimension(this._topInPx, this._heightInPx, window.innerHeight) + PX;
	}

	get Left() {
		return this.getNormalizedDimension(this._leftInPx, this._widthInPx, window.innerWidth) + PX;
	}

	protected getNormalizedDimension(dimensionOffset, popupDimension, windowDimension) {
		if (dimensionOffset < 0 || popupDimension > windowDimension) {
			return 0;
		}

		let minGap=Math.min(Math.round(MIN_PERCENT_GAP*windowDimension),MIN_PX_GAP);

		if (dimensionOffset + popupDimension + minGap*2 > windowDimension) {
			return (Math.max(minGap,windowDimension - popupDimension));
		}

		if (dimensionOffset < minGap){
			return minGap;
		}
		return dimensionOffset;
	}

	get Height() {
		return this._heightInPx + PX;
	}

	get Width() {
		return this._widthInPx + PX;
	}

	headerDeltaDrag({offsetTop, offsetLeft}: DeltaEvent) {
		// need to set limits in case the drag&drop went beyond the screen
		this._topInPx = Math.min(Math.max(0,this._topInPx+offsetTop),window.innerHeight);
		this._leftInPx = Math.min(Math.max(0,this._leftInPx+offsetLeft),window.innerWidth);
	}

	dragEnded(){
		this.cachePosition();
	}

	ngOnDestroy() {
		this.clearWindowEventSubscription();
	 }

	protected clearWindowEventSubscription(): void {
		this.windowEventSubscription.unsubscribe();
	}

	topRightResize(e: DeltaEvent) {
		this.resizeTop(e.offsetTop);
		this.resizeRight(e.offsetLeft);
	}

	topLeftResize(e: DeltaEvent) {
		this.resizeTop(e.offsetTop);
		this.resizeLeft(e.offsetLeft);
	}

	bottomRightResize(e: DeltaEvent) {
		this.resizeBottom(e.offsetTop);
		this.resizeRight(e.offsetLeft);
	}

	bottomLeftResize(e: DeltaEvent) {
		this.resizeBottom(e.offsetTop);
		this.resizeLeft(e.offsetLeft);
	}

	public resizeTop(offsetTop) {
		if ((offsetTop<0) || (this._heightInPx- offsetTop >= this.removePX(this.minHeight))){
			this._topInPx += offsetTop;

			if (this._topInPx >= 0) {
				this._heightInPx -=  offsetTop;
			}
			this.cachePosition();
		}
	}

	public resizeBottom(offsetTop) {
		this._heightInPx = Math.max(GUARD_MIN_HEIGHT,this._heightInPx+offsetTop);
		this.cachePosition();
	}

	public resizeRight(offsetLeft) {
		this._widthInPx =  Math.max(GUARD_MIN_WIDTH,this._widthInPx+offsetLeft)
		this.cachePosition();
	}

	public resizeLeft(offsetLeft) {
		this._leftInPx = Math.max(0,this._leftInPx+offsetLeft);
		this._widthInPx =  Math.max(GUARD_MIN_WIDTH,this._widthInPx-offsetLeft)
		this.cachePosition();
	}

	protected cachePosition(){
		if (this.positionStore) {
			this.positionStore.setPoistion(this.positionToken, {
				topPx: this._topInPx,
				leftPx: this._leftInPx,
				size: {heightPx: this._heightInPx, widthPx: this._widthInPx}
			});
		}
	}
}
